import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from './client'
import { queryKeys } from '../lib/query'
import type { PipelineData, PipelineCard, LostReason } from '@1130-studio/shared'

// Types for API responses
type PipelineResponse = PipelineData

interface MoveCardData {
  type: 'lead' | 'project'
  targetStageId: string
  position: number
  projectName?: string
}

interface MoveCardResponse {
  converted?: boolean
  archived?: boolean
  requiresReason?: boolean
  newCard?: PipelineCard
}

interface LostReasonData {
  lostReason: LostReason
  lostComment?: string
}

// Fetch pipeline data
export function usePipeline() {
  return useQuery({
    queryKey: queryKeys.pipeline.all,
    queryFn: () => apiClient<PipelineResponse>('/pipeline'),
  })
}

// Move pipeline card mutation
export function useMovePipelineCard() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: MoveCardData }) =>
      apiClient<MoveCardResponse>(`/pipeline/${id}/move`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onMutate: async ({ id, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.pipeline.all })

      // Snapshot the previous value
      const previousPipeline = queryClient.getQueryData<PipelineData>(queryKeys.pipeline.all)

      // Optimistically update the pipeline data
      if (previousPipeline) {
        const newPipeline = { ...previousPipeline }
        const newCards = { ...newPipeline.cards }

        // Find and remove card from its current stage
        let movedCard: PipelineCard | undefined
        for (const stageId in newCards) {
          const index = newCards[stageId].findIndex((c) => c.id === id)
          if (index !== -1) {
            movedCard = newCards[stageId][index]
            newCards[stageId] = [
              ...newCards[stageId].slice(0, index),
              ...newCards[stageId].slice(index + 1),
            ]
            break
          }
        }

        // Add card to new stage at new position
        if (movedCard) {
          if (!newCards[data.targetStageId]) {
            newCards[data.targetStageId] = []
          }
          const updatedCard = { ...movedCard, stageId: data.targetStageId, position: data.position }
          newCards[data.targetStageId] = [
            ...newCards[data.targetStageId].slice(0, data.position),
            updatedCard,
            ...newCards[data.targetStageId].slice(data.position),
          ]
        }

        newPipeline.cards = newCards
        queryClient.setQueryData(queryKeys.pipeline.all, newPipeline)
      }

      return { previousPipeline }
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousPipeline) {
        queryClient.setQueryData(queryKeys.pipeline.all, context.previousPipeline)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pipeline.all })
    },
  })
}

// Update card details mutation
export function useUpdatePipelineCard() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { type: 'lead' | 'project'; specification?: string; notes?: string } }) =>
      apiClient(`/pipeline/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pipeline.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.leads.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
    },
  })
}

// Update lost reason mutation
export function useUpdateLostReason() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: LostReasonData }) =>
      apiClient(`/pipeline/${id}/lost-reason`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pipeline.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.leads.all })
    },
  })
}
